import React, { useState, useEffect } from 'react';
import { View, ScrollView, StyleSheet, Dimensions } from 'react-native';
import {
  Card,
  Text,
  Button,
  Chip,
  Surface,
  useTheme,
  ProgressBar,
  List,
  Avatar,
  Badge,
  Divider,
  IconButton,
  Portal,
  Dialog,
  TextInput,
  Switch,
  Slider,
} from 'react-native-paper';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useNavigation } from '@react-navigation/native';
import {
  Brain,
  BarChart3,
  Target,
  Clock,
  BookOpen,
  Users,
  Zap,
  CheckCircle,
  ArrowRight,
  Sparkles,
  TrendingUp,
  Edit,
  Plus,
  Trash2,
  RefreshCw,
  Lightbulb,
  AlertCircle,
  Star,
  Calendar,
  Timer,
  Coffee,
  Filter,
  Search,
  Activity,
  Gauge,
  TrendingDown,
  Minus,
  Equal,
  PieChart,
  LineChart,
  BarChart,
} from 'lucide-react-native';

interface AnalyticsMetric {
  id: string;
  name: string;
  value: number;
  unit: string;
  change: number;
  changeType: 'increase' | 'decrease' | 'stable';
  trend: 'up' | 'down' | 'stable';
  confidence: number;
  lastUpdated: string;
}

interface LearningInsight {
  id: string;
  type:
    | 'performance'
    | 'engagement'
    | 'efficiency'
    | 'retention'
    | 'optimization';
  title: string;
  description: string;
  impact: 'high' | 'medium' | 'low';
  confidence: number;
  action: string;
  estimatedImprovement: number;
  category: string;
}

interface PredictiveAnalysis {
  id: string;
  metric: string;
  currentValue: number;
  predictedValue: number;
  timeframe: string;
  confidence: number;
  factors: string[];
  recommendation: string;
}

interface CohortAnalysis {
  id: string;
  cohort: string;
  size: number;
  retentionRate: number;
  engagementScore: number;
  completionRate: number;
  avgTimeToComplete: number;
  topPerformingFeatures: string[];
}

const { width } = Dimensions.get('window');

export function AIAnalyticsScreen() {
  const theme = useTheme();
  const insets = useSafeAreaInsets();
  const navigation = useNavigation();

  const [selectedMetric, setSelectedMetric] = useState<AnalyticsMetric | null>(
    null
  );
  const [showInsightsDialog, setShowInsightsDialog] = useState(false);
  const [showPredictionsDialog, setShowPredictionsDialog] = useState(false);
  const [selectedTimeframe, setSelectedTimeframe] = useState<string>('7d');

  // Mock analytics data
  const [analyticsMetrics, setAnalyticsMetrics] = useState<AnalyticsMetric[]>([
    {
      id: 'metric-1',
      name: 'Learning Velocity',
      value: 0.23,
      unit: 'skills/week',
      change: 0.05,
      changeType: 'increase',
      trend: 'up',
      confidence: 0.94,
      lastUpdated: '2024-01-15',
    },
    {
      id: 'metric-2',
      name: 'Engagement Rate',
      value: 87.3,
      unit: '%',
      change: -2.1,
      changeType: 'decrease',
      trend: 'down',
      confidence: 0.89,
      lastUpdated: '2024-01-15',
    },
    {
      id: 'metric-3',
      name: 'Retention Rate',
      value: 82.7,
      unit: '%',
      change: 1.8,
      changeType: 'increase',
      trend: 'up',
      confidence: 0.91,
      lastUpdated: '2024-01-15',
    },
    {
      id: 'metric-4',
      name: 'Completion Rate',
      value: 76.4,
      unit: '%',
      change: 3.2,
      changeType: 'increase',
      trend: 'up',
      confidence: 0.87,
      lastUpdated: '2024-01-15',
    },
    {
      id: 'metric-5',
      name: 'Cognitive Load',
      value: 0.65,
      unit: 'optimal',
      change: -0.08,
      changeType: 'decrease',
      trend: 'down',
      confidence: 0.92,
      lastUpdated: '2024-01-15',
    },
    {
      id: 'metric-6',
      name: 'Skill Mastery',
      value: 68.9,
      unit: '%',
      change: 4.7,
      changeType: 'increase',
      trend: 'up',
      confidence: 0.88,
      lastUpdated: '2024-01-15',
    },
  ]);

  const [learningInsights, setLearningInsights] = useState<LearningInsight[]>([
    {
      id: 'insight-1',
      type: 'performance',
      title: 'Video Content Performance Spike',
      description:
        'Your performance with video content increased by 23% this week, significantly above the 8% average improvement',
      impact: 'high',
      confidence: 0.94,
      action: 'Increase video content allocation in your learning path',
      estimatedImprovement: 15,
      category: 'Content Optimization',
    },
    {
      id: 'insight-2',
      type: 'engagement',
      title: 'Morning Learning Peak Confirmed',
      description:
        'Consistent pattern shows 34% higher engagement during 7-9 AM sessions compared to other times',
      impact: 'high',
      confidence: 0.91,
      action: 'Schedule complex topics for morning hours to maximize retention',
      estimatedImprovement: 25,
      category: 'Timing Optimization',
    },
    {
      id: 'insight-3',
      type: 'efficiency',
      title: 'Pour-Over Skill Acceleration',
      description:
        'Your pour-over brewing skills are developing 2.3x faster than the cohort average',
      impact: 'medium',
      confidence: 0.87,
      action: 'Consider advanced pour-over techniques to maintain momentum',
      estimatedImprovement: 12,
      category: 'Skill Development',
    },
    {
      id: 'insight-4',
      type: 'retention',
      title: 'Spaced Repetition Effectiveness',
      description:
        'Content reviewed with 3-day intervals shows 18% better retention than daily review',
      impact: 'medium',
      confidence: 0.85,
      action: 'Adjust review schedule to optimize long-term retention',
      estimatedImprovement: 18,
      category: 'Learning Strategy',
    },
  ]);

  const [predictiveAnalysis, setPredictiveAnalysis] = useState<
    PredictiveAnalysis[]
  >([
    {
      id: 'prediction-1',
      metric: 'Skill Mastery',
      currentValue: 68.9,
      predictedValue: 78.2,
      timeframe: '4 weeks',
      confidence: 0.89,
      factors: [
        'Consistent practice schedule',
        'High engagement with video content',
        'Optimal difficulty progression',
      ],
      recommendation:
        'Maintain current learning pace and focus on practical application',
    },
    {
      id: 'prediction-2',
      metric: 'Completion Rate',
      currentValue: 76.4,
      predictedValue: 82.1,
      timeframe: '6 weeks',
      confidence: 0.84,
      factors: [
        'Improved time management',
        'Better content personalization',
        'Reduced cognitive overload',
      ],
      recommendation:
        'Continue with current difficulty level and add more interactive content',
    },
    {
      id: 'prediction-3',
      metric: 'Engagement Rate',
      currentValue: 87.3,
      predictedValue: 91.8,
      timeframe: '2 weeks',
      confidence: 0.92,
      factors: [
        'Enhanced content relevance',
        'Improved learning flow',
        'Better progress tracking',
      ],
      recommendation:
        'Focus on maintaining content quality and personalization',
    },
  ]);

  const [cohortAnalysis, setCohortAnalysis] = useState<CohortAnalysis[]>([
    {
      id: 'cohort-1',
      cohort: 'Beginner Pour-Over',
      size: 1247,
      retentionRate: 89.3,
      engagementScore: 87.6,
      completionRate: 76.8,
      avgTimeToComplete: 4.2,
      topPerformingFeatures: [
        'Video Tutorials',
        'Interactive Workshops',
        'Progress Tracking',
      ],
    },
    {
      id: 'cohort-2',
      cohort: 'Intermediate Espresso',
      size: 892,
      retentionRate: 84.7,
      engagementScore: 91.2,
      completionRate: 82.1,
      avgTimeToComplete: 6.8,
      topPerformingFeatures: [
        'Advanced Techniques',
        'Equipment Guides',
        'Tasting Sessions',
      ],
    },
    {
      id: 'cohort-3',
      cohort: 'Advanced Cupping',
      size: 456,
      retentionRate: 92.1,
      engagementScore: 94.8,
      completionRate: 88.9,
      avgTimeToComplete: 8.5,
      topPerformingFeatures: [
        'Professional Standards',
        'Peer Learning',
        'Certification Path',
      ],
    },
  ]);

  const handleMetricPress = (metric: AnalyticsMetric) => {
    setSelectedMetric(metric);
  };

  const getChangeIcon = (trend: AnalyticsMetric['trend']) => {
    switch (trend) {
      case 'up':
        return <TrendingUp size={16} color={theme.colors.primary} />;
      case 'down':
        return <TrendingDown size={16} color={theme.colors.error} />;
      case 'stable':
        return <Equal size={16} color={theme.colors.outline} />;
      default:
        return <Equal size={16} color={theme.colors.outline} />;
    }
  };

  const getChangeColor = (changeType: AnalyticsMetric['changeType']) => {
    switch (changeType) {
      case 'increase':
        return theme.colors.primary;
      case 'decrease':
        return theme.colors.error;
      case 'stable':
        return theme.colors.outline;
      default:
        return theme.colors.outline;
    }
  };

  const getImpactColor = (impact: LearningInsight['impact']) => {
    switch (impact) {
      case 'high':
        return theme.colors.primary;
      case 'medium':
        return theme.colors.secondary;
      case 'low':
        return theme.colors.outline;
      default:
        return theme.colors.outline;
    }
  };

  const getInsightTypeIcon = (type: LearningInsight['type']) => {
    switch (type) {
      case 'performance':
        return <Target size={20} color={theme.colors.primary} />;
      case 'engagement':
        return <Activity size={20} color={theme.colors.secondary} />;
      case 'efficiency':
        return <Gauge size={20} color={theme.colors.tertiary} />;
      case 'retention':
        return <Brain size={20} color={theme.colors.primary} />;
      case 'optimization':
        return <Sparkles size={20} color={theme.colors.secondary} />;
      default:
        return <BarChart3 size={20} color={theme.colors.outline} />;
    }
  };

  const renderMetricCard = (metric: AnalyticsMetric) => (
    <Card
      key={metric.id}
      style={styles.metricCard}
      onPress={() => handleMetricPress(metric)}
    >
      <Card.Content>
        <View style={styles.metricHeader}>
          <Text variant='titleMedium' style={styles.metricName}>
            {metric.name}
          </Text>
          <View style={styles.metricTrend}>
            {getChangeIcon(metric.trend)}
            <Text
              variant='bodySmall'
              style={[
                styles.metricChange,
                { color: getChangeColor(metric.changeType) },
              ]}
            >
              {metric.change > 0 ? '+' : ''}
              {metric.change}
              {metric.unit}
            </Text>
          </View>
        </View>

        <View style={styles.metricValue}>
          <Text variant='displaySmall' style={styles.metricMainValue}>
            {metric.value}
          </Text>
          <Text variant='titleMedium' style={styles.metricUnit}>
            {metric.unit}
          </Text>
        </View>

        <View style={styles.metricFooter}>
          <View style={styles.metricConfidence}>
            <Text variant='labelSmall'>AI Confidence</Text>
            <Text variant='bodySmall'>
              {Math.round(metric.confidence * 100)}%
            </Text>
          </View>
          <Text variant='bodySmall' style={styles.metricUpdated}>
            Updated: {metric.lastUpdated}
          </Text>
        </View>
      </Card.Content>
    </Card>
  );

  const renderInsightCard = (insight: LearningInsight) => (
    <Card key={insight.id} style={styles.insightCard}>
      <Card.Content>
        <View style={styles.insightHeader}>
          <View style={styles.insightIcon}>
            {getInsightTypeIcon(insight.type)}
          </View>
          <View style={styles.insightInfo}>
            <Text variant='titleMedium' style={styles.insightTitle}>
              {insight.title}
            </Text>
            <Chip
              mode='outlined'
              textStyle={{ color: getImpactColor(insight.impact) }}
              style={{ borderColor: getImpactColor(insight.impact) }}
              compact
            >
              {insight.impact} impact
            </Chip>
          </View>
          <Badge style={styles.confidenceBadge}>
            {Math.round(insight.confidence * 100)}%
          </Badge>
        </View>

        <Text variant='bodyMedium' style={styles.insightDescription}>
          {insight.description}
        </Text>

        <View style={styles.insightActions}>
          <View style={styles.insightAction}>
            <Text variant='labelSmall'>Recommended Action</Text>
            <Text variant='bodyMedium' style={styles.actionText}>
              {insight.action}
            </Text>
          </View>
          <View style={styles.insightImprovement}>
            <Text variant='labelSmall'>Estimated Improvement</Text>
            <Text variant='titleMedium' style={styles.improvementValue}>
              +{insight.estimatedImprovement}%
            </Text>
          </View>
        </View>

        <Chip mode='outlined' compact style={styles.categoryChip}>
          {insight.category}
        </Chip>
      </Card.Content>
    </Card>
  );

  const renderPredictionCard = (prediction: PredictiveAnalysis) => (
    <Card key={prediction.id} style={styles.predictionCard}>
      <Card.Content>
        <View style={styles.predictionHeader}>
          <Text variant='titleMedium' style={styles.predictionTitle}>
            {prediction.metric} Prediction
          </Text>
          <Chip mode='outlined' compact>
            {prediction.timeframe}
          </Chip>
        </View>

        <View style={styles.predictionValues}>
          <View style={styles.predictionValue}>
            <Text variant='labelSmall'>Current</Text>
            <Text variant='titleLarge'>{prediction.currentValue}</Text>
          </View>
          <View style={styles.predictionArrow}>
            <ArrowRight size={24} color={theme.colors.primary} />
          </View>
          <View style={styles.predictionValue}>
            <Text variant='labelSmall'>Predicted</Text>
            <Text variant='titleLarge' style={styles.predictedValue}>
              {prediction.predictedValue}
            </Text>
          </View>
        </View>

        <View style={styles.predictionFactors}>
          <Text variant='labelSmall' style={styles.factorsTitle}>
            Key Factors:
          </Text>
          {prediction.factors.map((factor, index) => (
            <View key={index} style={styles.factorItem}>
              <CheckCircle size={16} color={theme.colors.primary} />
              <Text variant='bodySmall' style={styles.factorText}>
                {factor}
              </Text>
            </View>
          ))}
        </View>

        <Text variant='bodyMedium' style={styles.predictionRecommendation}>
          {prediction.recommendation}
        </Text>

        <View style={styles.predictionConfidence}>
          <Text variant='labelSmall'>AI Confidence</Text>
          <ProgressBar
            progress={prediction.confidence}
            color={theme.colors.primary}
            style={styles.confidenceBar}
          />
          <Text variant='bodySmall'>
            {Math.round(prediction.confidence * 100)}%
          </Text>
        </View>
      </Card.Content>
    </Card>
  );

  const renderCohortCard = (cohort: CohortAnalysis) => (
    <Card key={cohort.id} style={styles.cohortCard}>
      <Card.Content>
        <View style={styles.cohortHeader}>
          <Text variant='titleMedium' style={styles.cohortTitle}>
            {cohort.cohort}
          </Text>
          <Text variant='bodySmall' style={styles.cohortSize}>
            {cohort.size} learners
          </Text>
        </View>

        <View style={styles.cohortMetrics}>
          <View style={styles.cohortMetric}>
            <Text variant='labelSmall'>Retention</Text>
            <Text variant='titleMedium' style={styles.cohortValue}>
              {cohort.retentionRate}%
            </Text>
          </View>
          <View style={styles.cohortMetric}>
            <Text variant='labelSmall'>Engagement</Text>
            <Text variant='titleMedium' style={styles.cohortValue}>
              {cohort.engagementScore}%
            </Text>
          </View>
          <View style={styles.cohortMetric}>
            <Text variant='labelSmall'>Completion</Text>
            <Text variant='titleMedium' style={styles.cohortValue}>
              {cohort.completionRate}%
            </Text>
          </View>
        </View>

        <View style={styles.cohortDetails}>
          <Text variant='labelSmall'>Avg Time to Complete</Text>
          <Text variant='bodyMedium'>{cohort.avgTimeToComplete} weeks</Text>
        </View>

        <View style={styles.topFeatures}>
          <Text variant='labelSmall' style={styles.featuresTitle}>
            Top Performing Features:
          </Text>
          <View style={styles.featuresList}>
            {cohort.topPerformingFeatures.map((feature, index) => (
              <Chip
                key={index}
                mode='outlined'
                compact
                style={styles.featureChip}
              >
                {feature}
              </Chip>
            ))}
          </View>
        </View>
      </Card.Content>
    </Card>
  );

  return (
    <View
      style={[styles.container, { backgroundColor: theme.colors.background }]}
    >
      {/* Info Header */}
      <Surface style={styles.header} elevation={2}>
        <View style={styles.headerContent}>
          <View style={styles.headerTitle}>
            <BarChart3 size={32} color={theme.colors.primary} />
            <View style={styles.titleContainer}>
              <Text variant='headlineMedium' style={styles.title}>
                AI Analytics
              </Text>
              <Text variant='bodyMedium' style={styles.subtitle}>
                Advanced insights powered by machine learning
              </Text>
            </View>
          </View>
        </View>
      </Surface>

      {/* Timeframe Selector */}
      <Surface style={styles.timeframeCard} elevation={1}>
        <View style={styles.timeframeHeader}>
          <Text variant='titleMedium' style={styles.timeframeTitle}>
            Timeframe
          </Text>
          <View style={styles.timeframeButtons}>
            {['1d', '7d', '30d', '90d'].map(timeframe => (
              <Button
                key={timeframe}
                mode={
                  selectedTimeframe === timeframe ? 'contained' : 'outlined'
                }
                onPress={() => setSelectedTimeframe(timeframe)}
                compact
                style={styles.timeframeButton}
              >
                {timeframe}
              </Button>
            ))}
          </View>
        </View>
      </Surface>

      {/* Content */}
      <ScrollView
        style={styles.content}
        contentContainerStyle={styles.contentContainer}
      >
        {/* Key Metrics */}
        <View style={styles.section}>
          <View style={styles.sectionHeader}>
            <Text variant='titleLarge' style={styles.sectionTitle}>
              Key Metrics
            </Text>
            <Button
              mode='outlined'
              onPress={() => setShowInsightsDialog(true)}
              compact
              icon='lightbulb'
            >
              Insights
            </Button>
          </View>

          <View style={styles.metricsGrid}>
            {analyticsMetrics.map(renderMetricCard)}
          </View>
        </View>

        {/* Learning Insights */}
        <View style={styles.section}>
          <Text variant='titleLarge' style={styles.sectionTitle}>
            AI Learning Insights
          </Text>

          {learningInsights.map(renderInsightCard)}
        </View>

        {/* Predictive Analysis */}
        <View style={styles.section}>
          <View style={styles.sectionHeader}>
            <Text variant='titleLarge' style={styles.sectionTitle}>
              Predictive Analysis
            </Text>
            <Button
              mode='outlined'
              onPress={() => setShowPredictionsDialog(true)}
              compact
              icon='trending-up'
            >
              View All
            </Button>
          </View>

          {predictiveAnalysis.map(renderPredictionCard)}
        </View>

        {/* Cohort Analysis */}
        <View style={styles.section}>
          <Text variant='titleLarge' style={styles.sectionTitle}>
            Cohort Analysis
          </Text>

          {cohortAnalysis.map(renderCohortCard)}
        </View>
      </ScrollView>

      {/* Metric Details Dialog */}
      {selectedMetric && (
        <Portal>
          <Dialog
            visible={!!selectedMetric}
            onDismiss={() => setSelectedMetric(null)}
            style={styles.dialog}
          >
            <Dialog.Title>{selectedMetric.name} Details</Dialog.Title>

            <Dialog.Content style={styles.dialogContent}>
              <ScrollView showsVerticalScrollIndicator={false}>
                <Surface style={styles.metricDetailCard} elevation={1}>
                  <View style={styles.metricDetailHeader}>
                    <Text
                      variant='titleMedium'
                      style={styles.metricDetailTitle}
                    >
                      Current Performance
                    </Text>
                  </View>

                  <View style={styles.metricDetailValue}>
                    <Text
                      variant='displayLarge'
                      style={styles.metricDetailMainValue}
                    >
                      {selectedMetric.value}
                    </Text>
                    <Text variant='titleLarge' style={styles.metricDetailUnit}>
                      {selectedMetric.unit}
                    </Text>
                  </View>

                  <View style={styles.metricDetailChange}>
                    <Text variant='titleMedium'>Change from Last Period</Text>
                    <View style={styles.changeDisplay}>
                      {getChangeIcon(selectedMetric.trend)}
                      <Text
                        variant='titleLarge'
                        style={[
                          styles.changeValue,
                          { color: getChangeColor(selectedMetric.changeType) },
                        ]}
                      >
                        {selectedMetric.change > 0 ? '+' : ''}
                        {selectedMetric.change}
                        {selectedMetric.unit}
                      </Text>
                    </View>
                  </View>

                  <View style={styles.metricDetailInfo}>
                    <View style={styles.detailInfoItem}>
                      <Text variant='labelSmall'>AI Confidence</Text>
                      <Text variant='titleMedium'>
                        {Math.round(selectedMetric.confidence * 100)}%
                      </Text>
                    </View>
                    <View style={styles.detailInfoItem}>
                      <Text variant='labelSmall'>Last Updated</Text>
                      <Text variant='bodyMedium'>
                        {selectedMetric.lastUpdated}
                      </Text>
                    </View>
                  </View>
                </Surface>
              </ScrollView>
            </Dialog.Content>

            <Dialog.Actions>
              <Button onPress={() => setSelectedMetric(null)}>Close</Button>
              <Button
                mode='contained'
                onPress={() => {
                  setSelectedMetric(null);
                  setShowInsightsDialog(true);
                }}
              >
                View Related Insights
              </Button>
            </Dialog.Actions>
          </Dialog>
        </Portal>
      )}

      {/* Insights Dialog */}
      <Portal>
        <Dialog
          visible={showInsightsDialog}
          onDismiss={() => setShowInsightsDialog(false)}
          style={styles.dialog}
        >
          <Dialog.Title>AI Learning Insights</Dialog.Title>

          <Dialog.Content style={styles.dialogContent}>
            <ScrollView showsVerticalScrollIndicator={false}>
              <Surface style={styles.insightsOverviewCard} elevation={1}>
                <View style={styles.insightsOverviewHeader}>
                  <Lightbulb size={24} color={theme.colors.secondary} />
                  <Text
                    variant='titleMedium'
                    style={styles.insightsOverviewTitle}
                  >
                    How AI Analyzes Your Learning
                  </Text>
                </View>

                <Text
                  variant='bodyMedium'
                  style={styles.insightsOverviewDescription}
                >
                  Our AI continuously analyzes your learning patterns,
                  performance metrics, and engagement data to provide actionable
                  insights for optimizing your coffee education journey.
                </Text>
              </Surface>

              {learningInsights.map(insight => (
                <Surface
                  key={insight.id}
                  style={styles.insightDetailCard}
                  elevation={1}
                >
                  <View style={styles.insightDetailHeader}>
                    <View style={styles.insightDetailIcon}>
                      {getInsightTypeIcon(insight.type)}
                    </View>
                    <View style={styles.insightDetailInfo}>
                      <Text
                        variant='titleMedium'
                        style={styles.insightDetailTitle}
                      >
                        {insight.title}
                      </Text>
                      <Chip
                        mode='outlined'
                        textStyle={{ color: getImpactColor(insight.impact) }}
                        style={{ borderColor: getImpactColor(insight.impact) }}
                        compact
                      >
                        {insight.impact} impact
                      </Chip>
                    </View>
                  </View>

                  <Text
                    variant='bodyMedium'
                    style={styles.insightDetailDescription}
                  >
                    {insight.description}
                  </Text>

                  <View style={styles.insightDetailActions}>
                    <View style={styles.insightDetailAction}>
                      <Text variant='labelMedium' style={styles.actionLabel}>
                        Recommended Action:
                      </Text>
                      <Text
                        variant='bodyMedium'
                        style={styles.actionDetailText}
                      >
                        {insight.action}
                      </Text>
                    </View>
                    <View style={styles.insightDetailImprovement}>
                      <Text
                        variant='labelMedium'
                        style={styles.improvementLabel}
                      >
                        Estimated Improvement:
                      </Text>
                      <Text
                        variant='titleMedium'
                        style={styles.improvementDetailValue}
                      >
                        +{insight.estimatedImprovement}%
                      </Text>
                    </View>
                  </View>
                </Surface>
              ))}
            </ScrollView>
          </Dialog.Content>

          <Dialog.Actions>
            <Button onPress={() => setShowInsightsDialog(false)}>Close</Button>
          </Dialog.Actions>
        </Dialog>
      </Portal>

      {/* Predictions Dialog */}
      <Portal>
        <Dialog
          visible={showPredictionsDialog}
          onDismiss={() => setShowPredictionsDialog(false)}
          style={styles.dialog}
        >
          <Dialog.Title>AI Predictive Analysis</Dialog.Title>

          <Dialog.Content style={styles.dialogContent}>
            <ScrollView showsVerticalScrollIndicator={false}>
              <Surface style={styles.predictionsOverviewCard} elevation={1}>
                <View style={styles.predictionsOverviewHeader}>
                  <TrendingUp size={24} color={theme.colors.primary} />
                  <Text
                    variant='titleMedium'
                    style={styles.predictionsOverviewTitle}
                  >
                    Future Learning Projections
                  </Text>
                </View>

                <Text
                  variant='bodyMedium'
                  style={styles.predictionsOverviewDescription}
                >
                  Our AI analyzes current trends, your learning patterns, and
                  historical data to predict future performance and recommend
                  optimal learning strategies.
                </Text>
              </Surface>

              {predictiveAnalysis.map(prediction => (
                <Surface
                  key={prediction.id}
                  style={styles.predictionDetailCard}
                  elevation={1}
                >
                  <View style={styles.predictionDetailHeader}>
                    <Text
                      variant='titleMedium'
                      style={styles.predictionDetailTitle}
                    >
                      {prediction.metric} - {prediction.timeframe}
                    </Text>
                    <Chip mode='outlined' compact>
                      {Math.round(prediction.confidence * 100)}% confidence
                    </Chip>
                  </View>

                  <View style={styles.predictionDetailValues}>
                    <View style={styles.predictionDetailValue}>
                      <Text variant='labelSmall'>Current</Text>
                      <Text variant='displaySmall'>
                        {prediction.currentValue}
                      </Text>
                    </View>
                    <View style={styles.predictionDetailArrow}>
                      <ArrowRight size={32} color={theme.colors.primary} />
                    </View>
                    <View style={styles.predictionDetailValue}>
                      <Text variant='labelSmall'>Predicted</Text>
                      <Text
                        variant='displaySmall'
                        style={styles.predictionDetailPredicted}
                      >
                        {prediction.predictedValue}
                      </Text>
                    </View>
                  </View>

                  <View style={styles.predictionDetailFactors}>
                    <Text
                      variant='titleMedium'
                      style={styles.factorsDetailTitle}
                    >
                      Key Contributing Factors:
                    </Text>
                    {prediction.factors.map((factor, index) => (
                      <View key={index} style={styles.factorDetailItem}>
                        <CheckCircle size={16} color={theme.colors.primary} />
                        <Text
                          variant='bodyMedium'
                          style={styles.factorDetailText}
                        >
                          {factor}
                        </Text>
                      </View>
                    ))}
                  </View>

                  <Text
                    variant='bodyMedium'
                    style={styles.predictionDetailRecommendation}
                  >
                    <Text
                      variant='labelMedium'
                      style={styles.recommendationLabel}
                    >
                      AI Recommendation:
                    </Text>{' '}
                    {prediction.recommendation}
                  </Text>
                </Surface>
              ))}
            </ScrollView>
          </Dialog.Content>

          <Dialog.Actions>
            <Button onPress={() => setShowPredictionsDialog(false)}>
              Close
            </Button>
          </Dialog.Actions>
        </Dialog>
      </Portal>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  header: {
    padding: 16,
    backgroundColor: theme => theme.colors.surface,
  },
  headerContent: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  headerTitle: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  titleContainer: {
    marginLeft: 16,
    flex: 1,
  },
  title: {
    fontWeight: 'bold',
    marginBottom: 4,
  },
  subtitle: {
    opacity: 0.7,
  },
  timeframeCard: {
    padding: 16,
    margin: 16,
    borderRadius: 12,
  },
  timeframeHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  timeframeTitle: {
    fontWeight: 'bold',
  },
  timeframeButtons: {
    flexDirection: 'row',
    gap: 8,
  },
  timeframeButton: {
    minWidth: 60,
  },
  content: {
    flex: 1,
  },
  contentContainer: {
    padding: 16,
    paddingBottom: 32,
  },
  section: {
    marginBottom: 24,
  },
  sectionHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
  },
  sectionTitle: {
    fontWeight: 'bold',
  },
  metricsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
  },
  metricCard: {
    flex: 1,
    minWidth: '45%',
    borderRadius: 12,
  },
  metricHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
  },
  metricName: {
    fontWeight: 'bold',
    flex: 1,
  },
  metricTrend: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  metricChange: {
    fontWeight: 'bold',
  },
  metricValue: {
    flexDirection: 'row',
    alignItems: 'baseline',
    marginBottom: 16,
  },
  metricMainValue: {
    fontWeight: 'bold',
    color: theme => theme.colors.primary,
  },
  metricUnit: {
    marginLeft: 8,
    opacity: 0.7,
  },
  metricFooter: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  metricConfidence: {
    alignItems: 'center',
  },
  metricUpdated: {
    opacity: 0.6,
    fontSize: 12,
  },
  insightCard: {
    marginBottom: 12,
    borderRadius: 12,
  },
  insightHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 16,
  },
  insightIcon: {
    marginRight: 16,
    alignItems: 'center',
    justifyContent: 'center',
    width: 48,
    height: 48,
    borderRadius: 24,
    backgroundColor: '#f0f0f0',
  },
  insightInfo: {
    flex: 1,
    marginRight: 12,
  },
  insightTitle: {
    fontWeight: 'bold',
    marginBottom: 8,
  },
  confidenceBadge: {
    backgroundColor: theme => theme.colors.primary,
  },
  insightDescription: {
    marginBottom: 16,
    opacity: 0.8,
    lineHeight: 20,
  },
  insightActions: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 16,
  },
  insightAction: {
    flex: 1,
    marginRight: 12,
  },
  actionText: {
    fontWeight: 'bold',
    marginTop: 4,
  },
  insightImprovement: {
    alignItems: 'center',
    flex: 1,
  },
  improvementValue: {
    color: theme => theme.colors.primary,
    fontWeight: 'bold',
    marginTop: 4,
  },
  categoryChip: {
    alignSelf: 'flex-start',
  },
  predictionCard: {
    marginBottom: 12,
    borderRadius: 12,
  },
  predictionHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
  },
  predictionTitle: {
    fontWeight: 'bold',
  },
  predictionValues: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 16,
  },
  predictionValue: {
    alignItems: 'center',
    flex: 1,
  },
  predictionArrow: {
    alignItems: 'center',
  },
  predictedValue: {
    color: theme => theme.colors.primary,
    fontWeight: 'bold',
  },
  predictionFactors: {
    marginBottom: 16,
  },
  factorsTitle: {
    fontWeight: 'bold',
    marginBottom: 8,
  },
  factorItem: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 6,
  },
  factorText: {
    marginLeft: 8,
    opacity: 0.8,
    flex: 1,
  },
  predictionRecommendation: {
    marginBottom: 16,
    opacity: 0.8,
    lineHeight: 20,
    fontStyle: 'italic',
  },
  predictionConfidence: {
    alignItems: 'center',
  },
  confidenceBar: {
    width: '100%',
    height: 8,
    borderRadius: 4,
    marginVertical: 8,
  },
  cohortCard: {
    marginBottom: 12,
    borderRadius: 12,
  },
  cohortHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
  },
  cohortTitle: {
    fontWeight: 'bold',
  },
  cohortSize: {
    opacity: 0.7,
  },
  cohortMetrics: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 16,
  },
  cohortMetric: {
    alignItems: 'center',
    flex: 1,
  },
  cohortValue: {
    fontWeight: 'bold',
    color: theme => theme.colors.primary,
  },
  cohortDetails: {
    alignItems: 'center',
    marginBottom: 16,
  },
  topFeatures: {
    marginBottom: 16,
  },
  featuresTitle: {
    fontWeight: 'bold',
    marginBottom: 8,
  },
  featuresList: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  featureChip: {
    marginBottom: 4,
  },
  dialog: {
    maxHeight: '90%',
  },
  dialogContent: {
    maxHeight: 500,
  },
  metricDetailCard: {
    padding: 16,
    marginBottom: 16,
    borderRadius: 12,
  },
  metricDetailHeader: {
    marginBottom: 16,
  },
  metricDetailTitle: {
    fontWeight: 'bold',
  },
  metricDetailValue: {
    flexDirection: 'row',
    alignItems: 'baseline',
    justifyContent: 'center',
    marginBottom: 24,
  },
  metricDetailMainValue: {
    fontWeight: 'bold',
    color: theme => theme.colors.primary,
  },
  metricDetailUnit: {
    marginLeft: 12,
    opacity: 0.7,
  },
  metricDetailChange: {
    alignItems: 'center',
    marginBottom: 24,
  },
  changeDisplay: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginTop: 8,
  },
  changeValue: {
    fontWeight: 'bold',
  },
  metricDetailInfo: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  detailInfoItem: {
    alignItems: 'center',
    flex: 1,
  },
  insightsOverviewCard: {
    padding: 16,
    marginBottom: 16,
    borderRadius: 12,
  },
  insightsOverviewHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
  },
  insightsOverviewTitle: {
    marginLeft: 12,
    fontWeight: 'bold',
  },
  insightsOverviewDescription: {
    opacity: 0.8,
    lineHeight: 20,
  },
  insightDetailCard: {
    padding: 16,
    marginBottom: 16,
    borderRadius: 12,
  },
  insightDetailHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 16,
  },
  insightDetailIcon: {
    marginRight: 16,
    alignItems: 'center',
    justifyContent: 'center',
    width: 48,
    height: 48,
    borderRadius: 24,
    backgroundColor: '#f0f0f0',
  },
  insightDetailInfo: {
    flex: 1,
    marginRight: 12,
  },
  insightDetailTitle: {
    fontWeight: 'bold',
    marginBottom: 8,
  },
  insightDetailDescription: {
    marginBottom: 16,
    opacity: 0.8,
    lineHeight: 20,
  },
  insightDetailActions: {
    marginBottom: 16,
  },
  insightDetailAction: {
    marginBottom: 12,
  },
  actionLabel: {
    fontWeight: 'bold',
    marginBottom: 4,
  },
  actionDetailText: {
    opacity: 0.8,
    lineHeight: 20,
  },
  insightDetailImprovement: {
    alignItems: 'center',
  },
  improvementLabel: {
    fontWeight: 'bold',
    marginBottom: 4,
  },
  improvementDetailValue: {
    color: theme => theme.colors.primary,
    fontWeight: 'bold',
  },
  predictionsOverviewCard: {
    padding: 16,
    marginBottom: 16,
    borderRadius: 12,
  },
  predictionsOverviewHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
  },
  predictionsOverviewTitle: {
    marginLeft: 12,
    fontWeight: 'bold',
  },
  predictionsOverviewDescription: {
    opacity: 0.8,
    lineHeight: 20,
  },
  predictionDetailCard: {
    padding: 16,
    marginBottom: 16,
    borderRadius: 12,
  },
  predictionDetailHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
  },
  predictionDetailTitle: {
    fontWeight: 'bold',
  },
  predictionDetailValues: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 24,
  },
  predictionDetailValue: {
    alignItems: 'center',
    flex: 1,
  },
  predictionDetailArrow: {
    alignItems: 'center',
  },
  predictionDetailPredicted: {
    color: theme => theme.colors.primary,
    fontWeight: 'bold',
  },
  predictionDetailFactors: {
    marginBottom: 16,
  },
  factorsDetailTitle: {
    fontWeight: 'bold',
    marginBottom: 8,
  },
  factorDetailItem: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 6,
  },
  factorDetailText: {
    marginLeft: 8,
    opacity: 0.8,
    flex: 1,
  },
  predictionDetailRecommendation: {
    opacity: 0.8,
    lineHeight: 20,
  },
  recommendationLabel: {
    fontWeight: 'bold',
  },
});
